// src/components/layout/Footer.tsx
import React, { useCallback, useRef } from "react";
import { ArrowUpRight, ArrowUp } from "lucide-react";
import { useTransition } from "@/context/TransitionContext";
import { useLenis } from "@/components/providers/LenisProvider";
import { logEvent } from "@/utils/analytics";

const FOOTER_NAV_ITEMS = [
  { path: "/work", label: "Work", analyticsKey: "work" },
  { path: "/experience", label: "Experience", analyticsKey: "experience" },
  { path: "/about", label: "About", analyticsKey: "about" },
] as const;

/**
 * Footer component — matches Figma node 1151-1622 (Breakpoint=Desktop)
 *
 * Structure (mirrors Figma sibling frames):
 *   <footer>
 *     <div class="bg-surface rounded-3xl">  ← Figma: Footer frame (1149:14864)
 *       Contact + Nav links
 *       SHAUNSCOTT wordmark
 *     </div>
 *     <div>                                 ← Figma: Container frame (1150:332) — sibling, outside card
 *       Copyright | NY love
 *     </div>
 *   </footer>
 */
const Footer: React.FC = () => {
  const { navigateWithTransition } = useTransition();
  const { scrollTo } = useLenis();
  const isNavigatingRef = useRef(false);

  const handleNavigation = useCallback(
    (path: string, analyticsKey: string) =>
      (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();
        if (isNavigatingRef.current) return;

        logEvent("footer", "nav_click", analyticsKey);
        isNavigatingRef.current = true;
        navigateWithTransition(path);

        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 1000);
      },
    [navigateWithTransition]
  );

  const handleExternalLinkClick = (linkName: string): void => {
    logEvent("footer", "external_link_click", linkName);
  };

  const handleContactClick = (contactType: string): void => {
    logEvent("footer", "contact_click", contactType);
  };

  const handleBackToTop = (): void => {
    logEvent("footer", "back_to_top_click", "back_to_top");
    scrollTo(0);
  };

  const year = new Date().getFullYear();

  return (
    <footer aria-label="Site footer" className="mt-10 md:mt-12 lg:mt-16">
      {/* ── Rounded card (Figma: Footer frame 1149:14864) ── */}
      <div className="bg-surface text-white rounded-md overflow-hidden">
        {/* Top: Contact info + Nav links */}
        <div className="px-6 md:px-12 lg:px-16 pt-8 sm:pt-12 md:pt-16 pb-6 md:pb-8 flex flex-col gap-8 md:gap-10 lg:flex-row lg:justify-between">
          {/* Left: Contact block */}
          <div className="flex flex-col gap-4 md:gap-5">
            <span className="text-overline-mobile md:text-overline uppercase text-muted">
              Contact
            </span>
            <div className="flex flex-col gap-1">
              <a
                href="tel:9174053530"
                className="nav-link-footer flex items-center gap-1.5 w-fit"
                onClick={() => handleContactClick("phone")}
              >
                +1 917.405-3530
              </a>
              <a
                href="mailto:shaun.scott.design@gmail.com"
                className="nav-link-footer flex items-center gap-1.5 w-fit break-all sm:break-normal"
                onClick={() => handleContactClick("email")}
              >
                shaun.scott.design@gmail.com
              </a>
            </div>
          </div>

          {/* Right: Nav + Social columns */}
          <div className="flex flex-col sm:flex-row gap-8 sm:gap-12 md:gap-16 lg:gap-20">
            {/* Main nav links — same routes + transitions as header */}
            <div className="flex flex-col gap-3">
              {FOOTER_NAV_ITEMS.map(({ path, label, analyticsKey }) => (
                <a
                  key={path}
                  href={path}
                  className="nav-link-footer w-fit"
                  onClick={handleNavigation(path, analyticsKey)}
                >
                  {label}
                </a>
              ))}
            </div>

            {/* Social + Back to top */}
            <div className="flex flex-col gap-3">
              <a
                href="https://www.linkedin.com/in/shaunscott01"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link-footer flex items-center gap-1.5 w-fit"
                onClick={() => handleExternalLinkClick("linkedin")}
              >
                LinkedIn <ArrowUpRight className="icon-xs" aria-hidden="true" />
              </a>
              <a
                href="https://www.instagram.com/this.is.shaun/"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link-footer flex items-center gap-1.5 w-fit"
                onClick={() => handleExternalLinkClick("instagram")}
              >
                Instagram <ArrowUpRight className="icon-xs" aria-hidden="true" />
              </a>
              <button
                type="button"
                onClick={handleBackToTop}
                className="nav-link-footer flex items-center gap-1.5 w-fit cursor-pointer"
                aria-label="Back to top"
              >
                Back to top <ArrowUp className="icon-xs" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        {/* SHAUNSCOTT wordmark — SVG, constrained by card padding */}
        <div
          className="px-6 md:px-12 lg:px-16 mt-10 md:mt-16 lg:mt-24 pb-8 md:pb-12"
          aria-hidden="true"
        >
          <img
            src="/assets/images/logo/logo_shaunscott.svg"
            alt=""
            className="w-full h-auto block select-none"
            draggable={false}
          />
        </div>
      </div>

      {/* ── Copyright bar — Figma: Container (1150:332), sibling outside the card ── */}
      <div className="py-4 md:py-5 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center text-muted text-sm">
        <p>
          © {year} Shaun Scott &nbsp;|&nbsp; UX + Product Design
        </p>
        <p className="text-balance sm:text-right">
          Made with love in NY &nbsp;| &nbsp; KNICKS in 5!
        </p>
      </div>
    </footer>
  );
};

export default Footer;
